import { EMPTY_ARRAY, FilteredCreditApplication, formatDate } from '@applyonline/shared'
import { addMonths } from '@if/core/date'
import { EPaymentFrequency, EPaymentPlan } from '@if/core/types'
import { useCallback, useMemo, useState } from 'react'
import { ComputeAmountForFrequencyDto, MonthlyPayment, useComputePaymentAmountForFrequency } from '../../api/compute-api'

export interface TermPaymentOption {
  firstPaymentOn?: string | null
  activationDate?: Date | null
  requestedAmount?: number | null
  paymentPlanId?: string | null
  includeInsurance?: boolean
}

const useTermPaymentsTable = (
  creditApp: FilteredCreditApplication | null,
  option?: TermPaymentOption,
): [MonthlyPayment[], boolean] => {
  const dto = useMemo<ComputeAmountForFrequencyDto | null>(() => {
    if (creditApp) {
      return {
        interestRate: creditApp.finalDecision.interestRate,
        stateIso: creditApp.applicant.province,
        paymentFrequency: EPaymentFrequency.Monthly,
        deliveryOn: formatDate(option?.activationDate ?? new Date()),
        firstPaymentOn: formatDate(option?.firstPaymentOn ?? addMonths(new Date(), 1)),
        maxPmtAmount: creditApp.finalDecision.maxPmtAmount,
        amountRequested:
          option?.requestedAmount ?? Math.min(creditApp.requestedLoanAmount, creditApp.finalDecision.maxAmountFinanced),
        includeInsurance: option?.includeInsurance ?? false,
        merchantId: creditApp.merchantId,
        paymentPlanId: EPaymentPlan.RegularDailyInterests,
      }
    }
    return null
  }, [option, creditApp])
  const [result, isLoading] = useComputePaymentAmountForFrequency(dto)
  const filteredTermAmountPairs = result?.monthlyTerms.filter(
    (ta) => ta.term <= (creditApp?.finalDecision?.maxTermDuration ?? 0),
  )
  return [filteredTermAmountPairs ?? EMPTY_ARRAY, isLoading]
}

export default useTermPaymentsTable
export const useSessionStorage = <T,>(keyName: string, initialValue: T) => {
  const savedInfo = sessionStorage.getItem(keyName)
  const initValue = savedInfo ? (JSON.parse(savedInfo) as T) : initialValue

  const [value, setValue] = useState<T>(initValue)

  const saveValue = useCallback(
    (newValue: T) => {
      const json = JSON.stringify(newValue)
      sessionStorage.setItem(keyName, json)
      setValue(newValue)
    },
    [keyName],
  )

  return [value, saveValue] as const
}
